import { Check, ChevronDown, Loader2, X } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Text } from '@/components/ui/catalyst/text';
import { useProviderServiceDestinationsRetrieveQuery } from '@/services/api/generated/providerDestinationsService';
import { useUpdateComponentMutation } from '@/services/api/hipaaPackageService';
import { DestinationScope, LEGACY_SCOPE_MAP } from '@/types/provider-destinations';

import { useRequestPage } from '../contexts/RequestPageContext';
import {
  getComponentMissingInfoItems,
  getComponentResolvedInfoItems,
} from './utils';

interface SummaryRowProps {
  label: string;
  main: ReactNode;
  side?: ReactNode;
}

const SummaryRow = ({ label, main, side }: SummaryRowProps) => (
  <div className="flex min-h-[46px] items-start gap-6 border-b border-zinc-100 px-6 py-3 font-sans last:border-b-0">
    <div className="w-[110px] shrink-0 pt-px">
      <Text className="font-sans text-[13px] font-medium leading-4 text-zinc-400">
        {label}
      </Text>
    </div>
    <div className="min-w-0 flex-1">{main}</div>
    {side && (
      <div className="w-[100px] shrink-0 pt-px text-right">{side}</div>
    )}
  </div>
);

const formatRecordLabel = (value: string) =>
  value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part, index) => {
      const normalized = part.toLowerCase();

      if (normalized === 'er') return 'ER';
      if (normalized === 'hipaa') return 'HIPAA';
      if (normalized === 'id') return 'ID';
      if (normalized === 'dob') return 'DOB';
      if (normalized === 'ssn') return 'SSN';
      if (index > 0 && ['of', 'and', 'or', 'to', 'via', 'for'].includes(normalized)) {
        return normalized;
      }

      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    })
    .join(' ');

const formatDate = (value: string | null | undefined) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const RecordBadge = ({ label }: { label: string }) => (
  <span className="inline-flex h-[22px] items-center rounded-[8px] bg-indigo-50 px-3 font-sans text-[12px] font-medium leading-4 text-indigo-700">
    {label}
  </span>
);

const StatusPill = ({
  label,
  tone = 'success',
}: {
  label: string;
  tone?: 'success' | 'warning' | 'neutral';
}) => {
  const styles = {
    success: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-700',
    neutral: 'bg-zinc-100 text-zinc-600',
  } as const;

  return (
    <span
      className={`inline-flex h-[18px] items-center rounded-full px-2.5 font-sans text-[12px] font-semibold leading-4 ${styles[tone]}`}
    >
      {label}
    </span>
  );
};

const MissingInfoPill = ({
  label,
  onDismiss,
  isPending = false,
}: {
  label: string;
  onDismiss?: () => void;
  isPending?: boolean;
}) => (
  <span className="inline-flex min-h-[22px] items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 font-sans text-[12px] font-semibold leading-4 text-amber-700">
    <span>{label}</span>
    {onDismiss ? (
      <button
        type="button"
        aria-label={`Mark ${label} as resolved`}
        onClick={onDismiss}
        disabled={isPending}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-amber-600 transition hover:bg-amber-100 hover:text-amber-800 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {isPending ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Check className="h-3 w-3" />
        )}
      </button>
    ) : null}
  </span>
);

const ResolvedInfoPill = ({ label }: { label: string }) => (
  <span className="inline-flex min-h-[22px] items-center rounded-full bg-emerald-50 px-2.5 py-1 font-sans text-[12px] font-semibold leading-4 text-emerald-700">
    {label}
  </span>
);

const ManualResolvedInfoPill = ({
  label,
  onDismiss,
  isPending = false,
}: {
  label: string;
  onDismiss: () => void;
  isPending?: boolean;
}) => (
  <span className="inline-flex min-h-[22px] items-center gap-1 rounded-full bg-zinc-100 px-2.5 py-1 font-sans text-[12px] font-semibold leading-4 text-zinc-700">
    <span>{label}</span>
    <button
      type="button"
      aria-label={`Move ${label} back to missing information`}
      onClick={onDismiss}
      disabled={isPending}
      className="inline-flex h-4 w-4 items-center justify-center rounded-full text-zinc-500 transition hover:bg-zinc-200 hover:text-zinc-800 disabled:cursor-not-allowed disabled:opacity-70"
    >
      {isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <X className="h-3 w-3" />
      )}
    </button>
  </span>
);

interface ReviewSummaryTableProps {
  onResolveSignature?: () => void;
}

export const ReviewSummaryTable = ({
  onResolveSignature,
}: ReviewSummaryTableProps = {}) => {
  const {
    matterId,
    clientName,
    requestInfo,
    request,
    provider,
    matter,
    primaryClient,
    matterContext,
    hasMissingSignature,
    packageData,
    refetchPackage,
  } = useRequestPage();
  const [pendingComponentIds, setPendingComponentIds] = useState<string[]>([]);
  const [isResolvedExpanded, setIsResolvedExpanded] = useState(false);
  const [manualResolvedLabelOverrides, setManualResolvedLabelOverrides] =
    useState<Record<string, string>>({});
  const [hasRetriedResolvedLabelRefresh, setHasRetriedResolvedLabelRefresh] =
    useState(false);
  const [updateComponent] = useUpdateComponentMutation();
  const providerServiceId = provider?.provider_service_ref_id ?? null;
  const requestWithCustomDates = request as typeof request & {
    service_start_date?: string | null;
    service_end_date?: string | null;
  };
  const destinationScope =
    request?.provided_records?.[0]
      ? LEGACY_SCOPE_MAP.toScope(request.provided_records[0]) ??
        DestinationScope.RECORDS
      : DestinationScope.RECORDS;
  const { data: destinationData } = useProviderServiceDestinationsRetrieveQuery(
    { providerServiceId: providerServiceId ?? '' },
    { skip: !providerServiceId },
  );

  const patientName = clientName || 'Unknown patient';
  const patientAddress = [
    primaryClient?.address?.line1,
    primaryClient?.address?.line2,
    [primaryClient?.address?.city, primaryClient?.address?.state]
      .filter(Boolean)
      .join(', '),
    primaryClient?.address?.postcode,
  ]
    .filter(Boolean)
    .join(' ');

  const patientDob =
    formatDate(matterContext?.clientDateOfBirth) ||
    formatDate(primaryClient?.date_of_birth);
  const ssnValue =
    primaryClient?.last_four_of_ssn ||
    primaryClient?.ssn?.slice(-4) ||
    null;

  const providerAddress =
    provider?.address ||
    [matter?.city, matter?.state].filter(Boolean).join(', ') ||
    null;

  const rawCustomServiceStartDate = requestWithCustomDates?.service_start_date;
  const rawCustomServiceEndDate = requestWithCustomDates?.service_end_date;
  const customServiceStartDate = formatDate(rawCustomServiceStartDate);
  const customServiceEndDate = formatDate(rawCustomServiceEndDate);
  const hasCustomServiceDates = Boolean(rawCustomServiceStartDate);

  let periodText = 'Unknown Start — Present';
  if (hasCustomServiceDates) {
    periodText = `${customServiceStartDate || 'Unknown Start'} — ${customServiceEndDate || 'Present'}`;
  } else if (matter?.date_of_loss) {
    periodText = `${formatDate(matter.date_of_loss)} — Present`;
  }

  let periodLabel = 'Missing';
  if (hasCustomServiceDates) {
    periodLabel = 'Custom Dates of Service';
  } else if (matter?.date_of_loss) {
    periodLabel = 'Date of Loss';
  }
  const periodTone =
    hasCustomServiceDates || matter?.date_of_loss ? 'neutral' : 'warning';

  const deliveryText = (() => {
    if (!request?.channel) return 'No delivery method configured';

    switch (request.channel.type) {
      case 'fax':
        return request.channel.fax
          ? `Fax to ${request.channel.fax}`
          : 'Fax number missing';
      case 'email':
        return request.channel.email
          ? `Email to ${request.channel.email}`
          : 'Email address missing';
      case 'mail':
        return request.channel.address
          ? `Mail to ${request.channel.address}`
          : 'Mailing address missing';
      case 'portal':
        return 'Provider portal';
      default:
        return 'No delivery method configured';
    }
  })();

  const deliveryVerification = (() => {
    if (!request?.channel) {
      return {
        label: 'Missing',
        tone: 'warning' as const,
      };
    }

    const contactPointId = request.channel.provider_directory_contact_point_id;
    if (!contactPointId || !destinationData) {
      return {
        label: 'Manual Entry',
        tone: 'neutral' as const,
      };
    }

    const scopeBlock = destinationData.scopes.find(
      (scopeEntry) => scopeEntry.scope === destinationScope,
    );

    const matchingDestination = [
      scopeBlock?.recommended,
      ...(scopeBlock?.alternates ?? []),
    ].find(
      (destination) =>
        destination &&
        String(destination.contact_point_id) === String(contactPointId),
    );

    if (
      typeof matchingDestination?.evidence_summary === 'string' &&
      matchingDestination.evidence_summary.trim().toLowerCase() ===
        'manual entry'
    ) {
      return { label: 'Manual Entry', tone: 'neutral' as const };
    }

    if (matchingDestination?.verification_status === 'VERIFIED') {
      return { label: 'Verified', tone: 'success' as const };
    }

    return { label: 'Unverified', tone: 'warning' as const };
  })();

  const missingItems: Array<
    | {
        key: string;
        label: string;
      }
    | ReturnType<typeof getComponentMissingInfoItems>[number]
  > = [];
  if (!request?.channel) {
    missingItems.push({ key: 'request-method', label: 'request method' });
  } else if (request.channel.type === 'fax' && !request.channel.fax) {
    missingItems.push({ key: 'fax-number', label: 'fax number' });
  } else if (request.channel.type === 'email' && !request.channel.email) {
    missingItems.push({ key: 'email-address', label: 'email address' });
  } else if (request.channel.type === 'mail' && !request.channel.address) {
    missingItems.push({ key: 'mailing-address', label: 'mailing address' });
  }

  if (hasMissingSignature) {
    missingItems.push({ key: 'hipaa-signature', label: 'HIPAA signature' });
  }

  if (!matter?.date_of_loss && !hasCustomServiceDates) {
    missingItems.push({ key: 'date-of-loss', label: 'date of loss' });
  }

  const componentMissingItems = useMemo(
    () => getComponentMissingInfoItems(packageData?.components),
    [packageData?.components],
  );
  const hasUnlabeledManualResolvedItems = useMemo(
    () =>
      (packageData?.components ?? []).some((component) => {
        const resolvedMissingFields = component.resolved_missing_fields ?? [];
        const resolvedMissingFieldLabels =
          component.resolved_missing_field_labels ?? {};

        return resolvedMissingFields.some((fieldId) => {
          if (resolvedMissingFieldLabels[fieldId]) {
            return false;
          }

          return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
            fieldId,
          );
        });
      }),
    [packageData?.components],
  );
  const resolvedItems = useMemo(() => {
    const automaticItems: Array<{
      key: string;
      baseLabel: string;
      componentTitle?: string;
      source: 'automatic';
    }> = [];

    if (request?.channel) {
      switch (request.channel.type) {
        case 'fax':
          if (request.channel.fax) {
            automaticItems.push({
              key: 'resolved-fax-number',
              baseLabel: 'fax number',
              source: 'automatic',
            });
          }
          break;
        case 'email':
          if (request.channel.email) {
            automaticItems.push({
              key: 'resolved-email-address',
              baseLabel: 'email address',
              source: 'automatic',
            });
          }
          break;
        case 'mail':
          if (request.channel.address) {
            automaticItems.push({
              key: 'resolved-mailing-address',
              baseLabel: 'mailing address',
              source: 'automatic',
            });
          }
          break;
        case 'portal':
          automaticItems.push({
            key: 'resolved-request-method',
            baseLabel: 'request method',
            source: 'automatic',
          });
          break;
        default:
          break;
      }
    }

    if (!hasMissingSignature) {
        automaticItems.push({
          key: 'resolved-hipaa-signature',
          baseLabel: 'HIPAA signature',
          source: 'automatic',
        });
    }

    if (hasCustomServiceDates) {
        automaticItems.push({
          key: 'resolved-dates-of-service',
          baseLabel: 'dates of service',
          source: 'automatic',
        });
      } else if (matter?.date_of_loss) {
        automaticItems.push({
          key: 'resolved-date-of-loss',
          baseLabel: 'date of loss',
          source: 'automatic',
        });
      }

    const manualItems = getComponentResolvedInfoItems(packageData?.components).map(
      (item) => ({
        key: item.key,
        baseLabel: manualResolvedLabelOverrides[item.key] || item.baseLabel,
        componentTitle: item.componentTitle,
        componentId: item.componentId,
        fieldId: item.fieldId,
        resolvedMissingFields: item.resolvedMissingFields,
        source: 'manual' as const,
      }),
    );

    const items = [...automaticItems, ...manualItems];
    const labelCounts = items.reduce<Map<string, number>>((counts, item) => {
      counts.set(item.baseLabel, (counts.get(item.baseLabel) ?? 0) + 1);
      return counts;
    }, new Map());

      return items.map((item) => ({
        ...item,
        label:
          (labelCounts.get(item.baseLabel) ?? 0) > 1 && item.componentTitle
            ? `${item.baseLabel} • ${item.componentTitle}`
            : item.baseLabel,
      }));
  }, [
    hasCustomServiceDates,
    hasMissingSignature,
    manualResolvedLabelOverrides,
    matter?.date_of_loss,
    packageData?.components,
    request?.channel,
  ]);
  missingItems.push(...componentMissingItems);

  useEffect(() => {
    if (!hasUnlabeledManualResolvedItems || hasRetriedResolvedLabelRefresh) {
      return;
    }

    setHasRetriedResolvedLabelRefresh(true);
    const refreshLabels = async () => {
      await refetchPackage();
    };

    refreshLabels();
  }, [
    hasRetriedResolvedLabelRefresh,
    hasUnlabeledManualResolvedItems,
    refetchPackage,
  ]);

  const hasUploadedSignature = Boolean(
    packageData?.components?.some(
      (component) => component.component_type === 'signed_authorization_merge',
    ),
  );

  let signatureText = 'Signature ready';
  if (hasMissingSignature) {
    signatureText = 'Signature Missing';
  } else if (hasUploadedSignature) {
    signatureText = 'Uploaded Signature';
  } else {
    signatureText = 'Signature Ready';
  }

  const handleResolveMissingItem = async (
    missingItem: ReturnType<typeof getComponentMissingInfoItems>[number],
  ) => {
    if (
      !packageData?.id ||
      !missingItem.canDismiss ||
      pendingComponentIds.includes(missingItem.componentId)
    ) {
      return;
    }

    setPendingComponentIds((current) => [...current, missingItem.componentId]);
    setManualResolvedLabelOverrides((current) => ({
      ...current,
      [missingItem.key]: missingItem.baseLabel,
    }));
    try {
      await updateComponent({
        ...requestInfo,
        packageId: packageData.id,
        id: missingItem.componentId,
        patchedUpdatePackageComponent: {
          resolved_missing_fields: [
            ...new Set([
              ...missingItem.resolvedMissingFields,
              missingItem.fieldId,
            ]),
          ],
        },
      }).unwrap();
      await refetchPackage();
      toast.success(`Marked ${missingItem.baseLabel} as resolved.`);
    } catch {
      toast.error('Failed to update the missing information list');
    } finally {
      setPendingComponentIds((current) =>
        current.filter((componentId) => componentId !== missingItem.componentId),
      );
    }
  };

  const handleReopenResolvedItem = async (
    resolvedItem: Extract<
      (typeof resolvedItems)[number],
      { source: 'manual' }
    >,
  ) => {
    if (
      !packageData?.id ||
      pendingComponentIds.includes(resolvedItem.componentId)
    ) {
      return;
    }

    setPendingComponentIds((current) => [...current, resolvedItem.componentId]);
    try {
      await updateComponent({
        ...requestInfo,
        packageId: packageData.id,
        id: resolvedItem.componentId,
        patchedUpdatePackageComponent: {
          resolved_missing_fields: resolvedItem.resolvedMissingFields.filter(
            (fieldId) => fieldId !== resolvedItem.fieldId,
          ),
        },
      }).unwrap();
      await refetchPackage();
      toast.success(`Moved ${resolvedItem.baseLabel} back to missing information.`);
    } catch {
      toast.error('Failed to update the resolved information list');
    } finally {
      setPendingComponentIds((current) =>
        current.filter((componentId) => componentId !== resolvedItem.componentId),
      );
    }
  };

  return (
    <div className="overflow-hidden rounded-[10px] border border-zinc-200 bg-white font-sans">
      <SummaryRow
        label="Patient"
        main={
          <div className="space-y-1">
            <Text className="font-sans text-[14px] font-medium leading-[18px] text-zinc-900">
              {patientName}
            </Text>
            <Text className="font-sans text-[13px] leading-4 text-zinc-500">
              {patientAddress || 'Address not entered'}
            </Text>
          </div>
        }
        side={
          <div className="space-y-0.5 font-sans text-[13px] leading-4 text-zinc-500">
            {patientDob && <div>DOB {patientDob}</div>}
            {ssnValue && <div>SSN ••• {ssnValue}</div>}
          </div>
        }
      />

      <SummaryRow
        label="Provider"
        main={
          <div className="space-y-1">
            <Text className="font-sans text-[14px] font-medium leading-[18px] text-zinc-900">
              {provider?.name || 'Unknown provider'}
            </Text>
            {providerAddress ? (
              <Text className="font-sans text-[13px] leading-4 text-zinc-500">
                {providerAddress}
              </Text>
            ) : null}
          </div>
        }
      />

      <SummaryRow
        label="Records"
        main={
          request?.provided_records?.length ? (
            <div className="flex flex-wrap gap-2">
              {request.provided_records.map((record) => (
                <RecordBadge key={record} label={formatRecordLabel(record)} />
              ))}
            </div>
          ) : (
            <Text className="font-sans text-[13px] leading-4 text-zinc-500">
              No scopes selected
            </Text>
          )
        }
      />

      <SummaryRow
        label="Period"
        main={
          <Text className="font-sans text-[14px] font-medium leading-[18px] text-zinc-900">
            {periodText}
          </Text>
        }
        side={
          <StatusPill
            label={periodLabel}
            tone={periodTone}
          />
        }
      />

      <SummaryRow
        label="Delivery"
        main={
          <Text className="font-sans text-[14px] font-medium leading-[18px] text-zinc-900">
            {deliveryText}
          </Text>
        }
        side={
          <StatusPill
            label={deliveryVerification.label}
            tone={deliveryVerification.tone}
          />
        }
      />

      <SummaryRow
        label="Signature"
        main={
          hasMissingSignature ? (
            <div className="flex flex-wrap items-center gap-3">
              {onResolveSignature ? (
                <button
                  type="button"
                  onClick={onResolveSignature}
                  className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1.5 font-sans text-[12px] font-semibold leading-4 text-indigo-700 transition hover:bg-indigo-100"
                >
                  Find in Matter
                </button>
              ) : null}
              <a
                href={`/matters/${matterId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex font-sans text-[13px] font-medium leading-4 text-zinc-500 underline decoration-zinc-300 underline-offset-2 transition hover:text-zinc-700"
              >
                Matter overview
              </a>
            </div>
          ) : (
            <Text className="font-sans text-[14px] font-medium leading-[18px] text-zinc-900">
              {signatureText}
            </Text>
          )
        }
        side={
          <StatusPill
            label={hasMissingSignature ? 'Missing' : 'Ready'}
            tone={hasMissingSignature ? 'warning' : 'success'}
          />
        }
      />

      <SummaryRow
        label="Missing Information"
        main={
          missingItems.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {missingItems.map((item) => (
                <MissingInfoPill
                  key={item.key}
                  label={item.label}
                  onDismiss={
                    'componentId' in item && item.canDismiss
                      ? () => {
                          handleResolveMissingItem(item);
                        }
                      : undefined
                  }
                  isPending={
                    'componentId' in item &&
                    pendingComponentIds.includes(item.componentId)
                  }
                />
              ))}
            </div>
          ) : (
            <Text className="font-sans text-[13px] leading-4 text-zinc-500">
              No missing information detected.
            </Text>
          )
        }
      />

      {resolvedItems.length > 0 ? (
        <SummaryRow
          label="Resolved"
          main={
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setIsResolvedExpanded((current) => !current)}
                className="inline-flex items-center gap-2 rounded-md font-sans text-[13px] font-medium leading-4 text-zinc-700 transition hover:text-zinc-900"
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isResolvedExpanded ? 'rotate-180' : ''}`}
                />
                <span>
                  {isResolvedExpanded ? 'Hide' : 'Show'} resolved items
                </span>
              </button>

              {isResolvedExpanded ? (
                <div className="flex flex-wrap gap-2">
                  {resolvedItems.map((item) =>
                    item.source === 'manual' ? (
                      <ManualResolvedInfoPill
                        key={item.key}
                        label={item.label}
                        onDismiss={() => handleReopenResolvedItem(item)}
                        isPending={pendingComponentIds.includes(item.componentId)}
                      />
                    ) : (
                      <ResolvedInfoPill
                        key={item.key}
                        label={item.label}
                      />
                    ),
                  )}
                </div>
              ) : null}
            </div>
          }
          side={
            <StatusPill
              label={`${resolvedItems.length} resolved`}
              tone="success"
            />
          }
        />
      ) : null}
    </div>
  );
};
